import {
  Alert,
  Button,
  ButtonGroup,
  Checkboxes,
  DataStatus,
  Dialog,
  DialogFooter,
  Segment,
  TextInput,
  showNotification,
} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNil} from 'ramda';

import {
  newVehicleApi,
  usePutTrimFeatureAssignmentsMutation,
  useListTrimQuery,
  type FeatureResponseBody,
  type TrimFeatureAssignmentInput,
} from '@dms/api/new-vehicle';
import {getNewVehicleEnumLabel} from '@dms/features/new-vehicle';

import {handleCatalogueError, joinParts, t} from '../catalogueUi';
import {
  missingPriceFills,
  optionalPriceError,
  optionPriceRequired,
  priceInput,
} from '../moneyInput';
import {NewPricelistVersionDialog} from '../NewPricelistVersionDialog';
import {setTrimInclusion} from '../setTrimInclusion';
import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import {
  ITEM_CHANGE_CANCELLED,
  type PricedItemChangeOutcome,
} from '../types/PricedItemChangeOutcome';
import {usePublishedPriceSave} from '../usePublishedPriceSave';
import {isInPricelist} from '../utils/isInPricelist';
import {trimAssignmentEntry} from '../utils/trimAssignmentEntry';
import {trimFeatureChanges} from '../utils/trimFeatureChanges';
import {FeatureLibraryChoice} from './FeatureLibraryChoice';

type Inclusion = TrimFeatureAssignmentInput['inclusion'];

/** Adds a library item to a model year by assigning it to trims of the year. */
export function AddFromLibraryDialog(props: {
  modelYearId: string;
  yearFeatureIds: string[];
  onClose: () => void;
}) {
  const trims = useListTrimQuery({});
  const [putAssignments] = usePutTrimFeatureAssignmentsMutation();
  const [getTrim] = newVehicleApi.useLazyGetTrimQuery();
  const [getAssignments] = newVehicleApi.useLazyListTrimFeatureAssignmentsQuery();
  const publishedPrice = usePublishedPriceSave(props.modelYearId);
  const [feature, setFeature] = useState<FeatureResponseBody | null>(null);
  const [trimIds, setTrimIds] = useState<string[]>([]);
  const [inclusion, setInclusion] = useState<Inclusion>('STANDARD');
  const [prices, setPrices] = useState<Partial<Record<string, string>>>({});
  const parsePrice = (trimId: string) => priceInput.safeParse(prices[trimId] ?? '');
  const [isSaving, setSaving] = useState(false);
  // Two clicks in one task both run before isSaving re-renders the button disabled.
  const isSaveRunning = useRef(false);
  const [savedTrimIds, setSavedTrimIds] = useState<string[]>([]);
  const [hasFailed, setFailed] = useState(false);
  const isLocked = isSaving || savedTrimIds.length > 0;
  const yearTrims =
    trims.data?.items.filter((trim) => trim.modelYearId === props.modelYearId && !trim.archived) ??
    [];
  const chosenTrims = yearTrims.filter(({id}) => trimIds.includes(id));
  const prefillPrices = (ids: string[]) =>
    setPrices((current) => ({...current, ...missingPriceFills(current, ids)}));
  const savedTrimNames = yearTrims
    .filter(({id}) => savedTrimIds.includes(id))
    .map(({name}) => name)
    .join(', ');

  const assignedInclusion = (picked: FeatureResponseBody) =>
    picked.standaloneOrderable ? inclusion : 'STANDARD';
  /**
   * All chosen trims of a published year as one batch (D107): one request writes all or none, after
   * one pricelist version that lists the item on each online trim. A Draft trim lists no row.
   */
  const assignInOneBatch = async (picked: FeatureResponseBody) => {
    const entries: PricedItemBatchEntry[] = [];
    for (const trimId of trimIds) {
      const trim = await getTrim({trimId}, false).unwrap();
      const assignments = await getAssignments({trimId}, false).unwrap();
      const next = setTrimInclusion(
        assignments,
        picked.id,
        assignedInclusion(picked),
        assignedInclusion(picked) === 'OPTIONAL' ? (parsePrice(trimId).data ?? null) : null
      );
      entries.push(
        trimAssignmentEntry({
          trim,
          title: t('entity.newVehicle.catalogue.assignmentOnTrim', '{feature} on {trim}', {
            feature: picked.name,
            trim: trim.name,
          }),
          items: isInPricelist.trim(trim.publicationStatus)
            ? trimFeatureChanges({
                trimId,
                trimName: trim.name,
                stored: assignments,
                next,
                features: [picked],
                variantName: (variantId) => variantId,
              })
            : [],
          stored: assignments,
          next,
          readTrimVersion: () =>
            getTrim({trimId}, false)
              .unwrap()
              .then((fresh) => fresh.version),
          readAssignments: () => getAssignments({trimId}, false).unwrap(),
        })
      );
    }
    return [await publishedPrice.writePricedItemChanges({entries})];
  };
  /**
   * A draft year, one trim at a time: the backend rejects a trim's PUT as stale while another trim of the model
   * year is being written (DMS-NV-CT-0012), and a published year asks for each online trim's
   * pricelist version in turn (D102). A cancelled version stops before that trim.
   */
  const assignToTrims = async (picked: FeatureResponseBody) => {
    const outcomes: PricedItemChangeOutcome[] = [];
    for (const trimId of trimIds.filter((id) => !savedTrimIds.includes(id))) {
      const trim = await getTrim({trimId}, false).unwrap();
      const assignments = await getAssignments({trimId}, false).unwrap();
      const next = setTrimInclusion(
        assignments,
        picked.id,
        assignedInclusion(picked),
        assignedInclusion(picked) === 'OPTIONAL' ? (parsePrice(trimId).data ?? null) : null
      );
      const outcome = await publishedPrice.writePricedItemChange({
        // A Draft trim is outside the price list: it takes the item without a version.
        items: isInPricelist.trim(trim.publicationStatus)
          ? trimFeatureChanges({
              trimId,
              trimName: trim.name,
              stored: assignments,
              next,
              features: [picked],
              variantName: (variantId) => variantId,
            })
          : [],
        write: (pricelistVersion) =>
          putAssignments({
            trimId,
            replaceTrimFeatureAssignmentsRequestBody: {
              version: trim.version,
              assignments: next,
              ...(pricelistVersion && {pricelistVersion}),
            },
          }).unwrap(),
      });
      outcomes.push(outcome);
      if (outcome === ITEM_CHANGE_CANCELLED) {
        break;
      }
      setSavedTrimIds((current) => [...current, trimId]);
    }
    return outcomes;
  };
  const save = (picked: FeatureResponseBody) => {
    if (isSaveRunning.current) {
      return;
    }
    isSaveRunning.current = true;
    setSaving(true);
    setFailed(false);
    publishedPrice
      .readIsPublished()
      .then((isPublished) => (isPublished ? assignInOneBatch(picked) : assignToTrims(picked)))
      .then((outcomes) => {
        if (outcomes.includes(ITEM_CHANGE_CANCELLED)) {
          setFailed(true);
          return;
        }
        // A trim saved with a new pricelist version already said "Saved. Pricelist is now {label}."
        if (outcomes.every((outcome) => outcome === 'written')) {
          showNotification.success(
            t('entity.newVehicle.catalogue.assignmentsSaved', 'Trim assignments saved')
          );
        }
        props.onClose();
      })
      .catch((error: unknown) => {
        setFailed(true);
        handleCatalogueError(error);
      })
      .finally(() => {
        isSaveRunning.current = false;
        setSaving(false);
      });
  };
  const pick = (picked: FeatureResponseBody) => {
    setFeature(picked);
    setSavedTrimIds([]);
    setFailed(false);
  };

  return (
    <Dialog
      isOpen
      withAdditionalFooter
      title={t('entity.newVehicle.catalogue.addFromLibrary', 'Add from library')}
      onClose={props.onClose}
    >
      <VStack spacing={4}>
        <Show when={hasFailed && savedTrimNames.length > 0}>
          <Alert
            variant="warning"
            message={t(
              'entity.newVehicle.catalogue.addFromLibraryPartlySaved',
              'Added on {trims} only. The other trims were not changed; select Add to try them again.',
              {trims: savedTrimNames}
            )}
          />
        </Show>
        <VStack spacing={1}>
          <FeatureLibraryChoice
            yearFeatures={[]}
            excludedIds={props.yearFeatureIds}
            label={t('entity.newVehicle.authoring.searchEquipment', 'Search equipment')}
            isLabelVisible
            placeholder={t(
              'entity.newVehicle.authoring.searchEquipmentPlaceholder',
              'Search equipment…'
            )}
            value={feature}
            isDisabled={isLocked}
            onPick={pick}
          />
          <Text size="xSmall" color="secondary">
            {feature
              ? joinParts([
                  getNewVehicleEnumLabel('featureCategory', feature.category, t),
                  feature.factoryCode,
                ])
              : t(
                  'entity.newVehicle.catalogue.libraryScopeHint',
                  'The library holds the equipment of every model.'
                )}
          </Text>
        </VStack>
        <Show when={feature}>
          {(picked) => (
            <VStack spacing={4}>
              <DataStatus
                isLoading={trims.isLoading}
                isError={trims.isError}
                isEmpty={yearTrims.length === 0}
                emptyMessage={t(
                  'entity.newVehicle.authoring.addTrimsFirst',
                  'Add trim levels first'
                )}
              >
                <Checkboxes
                  label={t('entity.newVehicle.authoring.availableTrims', 'Available on trims')}
                  direction="vertical"
                  options={yearTrims.map(({id, name}) => ({
                    value: id,
                    label: name,
                    isDisabled: isLocked,
                  }))}
                  value={trimIds}
                  onChange={(value) => {
                    const ids = value ?? [];
                    setTrimIds(ids);
                    prefillPrices(yearTrims.map(({id}) => id).filter((id) => ids.includes(id)));
                  }}
                />
              </DataStatus>
              <VStack spacing={1}>
                <Segment<Inclusion>
                  label={t('entity.newVehicle.authoring.inclusion', 'Inclusion')}
                  value={picked.standaloneOrderable ? inclusion : 'STANDARD'}
                  options={[
                    {
                      value: 'STANDARD',
                      label: t('entity.newVehicle.authoring.standardShort', 'Standard'),
                    },
                    {
                      value: 'OPTIONAL',
                      label: t('entity.newVehicle.authoring.optionalShort', 'Optional'),
                      isDisabled: !picked.standaloneOrderable,
                    },
                  ]}
                  isDisabled={isLocked}
                  onChange={setInclusion}
                />
                <Show when={assignedInclusion(picked) === 'OPTIONAL'}>
                  <VStack spacing={4}>
                    {chosenTrims.map((trim) => (
                      <TextInput
                        key={trim.id}
                        label={t(
                          'entity.newVehicle.catalogue.priceOnTrimNet',
                          'Price of {feature} on {trim}, net',
                          {feature: picked.name, trim: trim.name}
                        )}
                        value={prices[trim.id] ?? ''}
                        isDisabled={isSaving || savedTrimIds.includes(trim.id)}
                        isRequired
                        errorMessage={optionalPriceError(prices[trim.id])}
                        helperText={optionPriceRequired()}
                        onChange={(value) =>
                          setPrices((current) => ({...current, [trim.id]: value ?? ''}))
                        }
                        onBlur={() => prefillPrices(chosenTrims.map(({id}) => id))}
                      />
                    ))}
                  </VStack>
                </Show>
                <Show when={!picked.standaloneOrderable}>
                  <Text size="xSmall" color="secondary">
                    {t(
                      'entity.newVehicle.authoring.standardOnly',
                      'Standard equipment only. Add this item to a package to sell it as part of that package.'
                    )}
                  </Text>
                </Show>
              </VStack>
            </VStack>
          )}
        </Show>
      </VStack>
      <DialogFooter>
        <ButtonGroup align="right">
          <Button
            variant="secondary"
            title={t('general.actions.cancel', 'Cancel')}
            onClick={props.onClose}
          />
          <Button
            variant="primary"
            title={t('general.actions.add', 'Add')}
            isLoading={isSaving}
            isDisabled={
              isSaving ||
              isNil(feature) ||
              trimIds.length === 0 ||
              (assignedInclusion(feature) === 'OPTIONAL' &&
                trimIds.some((trimId) => !parsePrice(trimId).success))
            }
            onClick={() => feature && save(feature)}
          />
        </ButtonGroup>
      </DialogFooter>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </Dialog>
  );
}
